import React from "react";
import { Tooltip } from "antd";
import dayjs from "dayjs";

/**
 * Nhãn/format dùng CHUNG cho các tab của màn chi tiết đợt thưởng.
 * Gom về 1 chỗ để bảng phân bổ, bảng người tham gia và thẻ tổng quan
 * không mỗi nơi một màu/một chữ cho cùng trạng thái.
 */
export const ORDER_STATUS_META = {
    reserved: { color: "blue", hex: "#2563eb", label: "Đã giữ suất" },
    awarded: { color: "green", hex: "#16a34a", label: "Đã cộng ví" },
    out_of_budget: { color: "orange", hex: "#ea580c", label: "Trượt" },
    cancelled: { color: "default", hex: "#94a3b8", label: "Đơn huỷ" },
    // Chỉ đợt ứng trước mới có: đã ứng rồi đơn bị huỷ ⇒ trừ lại số dư.
    // Thiếu mục này thì bảng hiện thẳng chữ máy 'clawed_back' (154 dòng, 21/09/2026).
    clawed_back: { color: "red", hex: "#dc2626", label: "Đã thu hồi" },
};

/**
 * Đợt ỨNG TRƯỚC không phát thưởng — nó trả SỚM tiền hoàn của chính đơn. Dùng chung
 * chữ "Thưởng"/"Đã cộng ví" làm admin đọc thành "chi thêm", rồi cộng nhầm vào chi phí
 * khuyến mãi. Chữ riêng cho loại đợt này, phần còn lại giữ nguyên.
 */
const FAST_PAYOUT_STATUS_LABELS = { reserved: "Chờ ứng", awarded: "Đã ứng" };

/** Nhãn trạng thái theo LOẠI ĐỢT — luôn dùng hàm này thay cho ORDER_STATUS_META trực tiếp. */
export const orderStatusMeta = (program) => {
    if (program?.type !== "fast_payout") return ORDER_STATUS_META;
    return Object.fromEntries(
        Object.entries(ORDER_STATUS_META).map(([key, meta]) => [
            key,
            FAST_PAYOUT_STATUS_LABELS[key] ? { ...meta, label: FAST_PAYOUT_STATUS_LABELS[key] } : meta,
        ])
    );
};

/** Tiêu đề cột tiền — đợt ứng trước là "Tiền ứng", không phải "Thưởng". */
export const rewardAmountTitle = (program) => (program?.type === "fast_payout" ? "Tiền ứng" : "Thưởng");

export const PARTICIPANT_STATUS_META = {
    joined: { color: "default", label: "Đã đăng ký" },
    achieved: { color: "green", label: "Đã có thưởng" },
    capped: { color: "purple", label: "Chạm trần" },
};

export const PROGRAM_STATUS_META = {
    draft: { color: "default", label: "Nháp" },
    scheduled: { color: "blue", label: "Chờ tới ngày" },
    running: { color: "green", label: "Đang chạy" },
    ended: { color: "orange", label: "Đã hết hạn" },
    settled: { color: "purple", label: "Đã trả thưởng" },
};

export const CAMP_LABELS = { shopee: "Shopee", lazada: "Lazada", tiktok: "TikTok", shopeefood: "ShopeeFood" };

export const fmtMoney = (n) => Number(n || 0).toLocaleString("vi-VN") + "đ";
export const fmtNumber = (n) => Number(n || 0).toLocaleString("vi-VN");
export const fmtUnix = (ts, f = "DD/MM/YYYY HH:mm") => (ts ? dayjs.unix(Number(ts)).format(f) : "—");

/** Rút gọn tiền cho trục biểu đồ (1.2M / 350K). */
export const fmtShortMoney = (v) => {
    const n = Number(v) || 0;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return String(n);
};

/** totals[] từ API → object tra nhanh theo status. */
export const totalsByStatus = (totals = []) =>
    Object.fromEntries(totals.map((t) => [t.status, t]));

/**
 * Đợt có nhân/ứng cả phần Xtra hay không.
 * ⚠️ Giữ CÙNG LUẬT với backend services/reward-program/reward-commission-basis.js.
 */
export const programIncludesXtra = (program) =>
    program?.type === "fast_payout" || program?.config?.scope === "all";

/**
 * 2 cột hoa hồng — dùng CHUNG cho bảng Phân bổ đơn và bảng đơn của từng user.
 *
 * Tách "HH gốc" / "Xtra" thay vì 1 cột gộp: gộp thì đơn chỉ có Xtra hiện 0đ (admin
 * tưởng đơn không hoa hồng), mà cộng sẵn thì không biết số nào từ đâu ra. Cột Xtra
 * chú thích rõ đợt này CÓ tính phần đó không, để không ai trừ tay ra số sai.
 *
 * `xtraCommission` NULL = dòng ghi trước 21/09/2026, chưa tách được ⇒ hiện "—",
 * KHÁC hẳn 0đ (đơn thật sự không có Xtra).
 */
export const basisColumns = (program) => {
    const includesXtra = programIncludesXtra(program);
    return [
        {
            title: "HH gốc", dataIndex: "baseCommission", width: 110, align: "right",
            render: fmtMoney,
            onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
        },
        {
            title: (
                <Tooltip title={includesXtra ? "Đợt này CÓ tính phần Xtra" : "Đợt này KHÔNG tính phần Xtra vào mức thưởng"}>
                    <span className={includesXtra ? "" : "text-content-3"}>Xtra{includesXtra ? "" : " *"}</span>
                </Tooltip>
            ),
            dataIndex: "xtraCommission", width: 110, align: "right",
            render: (v) => (v == null ? "—" : <span className={includesXtra ? "" : "text-content-3"}>{fmtMoney(v)}</span>),
            onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
        },
    ];
};
